import {
  destroyJusbrasilCookie,
  getCookie,
  setJusbrasilCookie,
} from '@jusbrasil-web/shared-utils-cookies'

/**
 * Utilities for managing signup data in cookies.
 * Based on Bifrost implementation where signup data (name, occupation, password)
 * is stored in a cookie before redirecting to CPF signup page.
 *
 * Note: Uses `name` instead of `fullName` to match Bifrost's cookie structure.
 * This is a cookie-specific format related to `SignupFormFields` (see `api/types.ts`).
 */
export interface SignupData {
  name: string
  occupation: string
  password: string
}

const SIGNUP_DATA_COOKIE_NAME = 'signup_data'
const COOKIE_EXPIRY_MINUTES = 30

/**
 * Saves signup data to a cookie.
 * Cookie expires in 30 minutes (same as Bifrost).
 *
 * @param data - The signup data to save
 */
export function saveSignupDataToCookie(data: SignupData): void {
  if (typeof document === 'undefined') {
    return
  }

  const thirtyMinutesFromNow = new Date(Date.now() + COOKIE_EXPIRY_MINUTES * 60 * 1000)
  const cookieValue = JSON.stringify(data)

  setJusbrasilCookie(null, SIGNUP_DATA_COOKIE_NAME, cookieValue, {
    expires: thirtyMinutesFromNow,
  })
}

function decodeIfEncoded(s: string): string {
  if (!s.includes('%')) return s
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

/**
 * Reads signup data from cookie.
 *
 * @returns The signup data or null if not found/invalid
 */
export function readSignupDataFromCookie(): SignupData | null {
  if (typeof document === 'undefined') return null

  const raw = getCookie<string>(null, SIGNUP_DATA_COOKIE_NAME)
  if (!raw || typeof raw !== 'string') return null

  const value = decodeIfEncoded(raw)
  if (!value.trim().startsWith('{')) return null

  try {
    const data = JSON.parse(value) as SignupData
    return data?.name && data?.occupation && data?.password ? data : null
  } catch {
    return null
  }
}

/**
 * Clears the signup data cookie.
 */
export function clearSignupDataCookie(): void {
  if (typeof document === 'undefined') {
    return
  }

  destroyJusbrasilCookie(null, SIGNUP_DATA_COOKIE_NAME)
}
